
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getSocket } from '../ChatPage/socket';
import {
    getAdminRealtimeRuntimeDiagnostics,
} from './adminRealtimeUiBridge';
import { useAdminRealtimePanel } from './AdminRealtimePanelProvider';

const DIAGNOSTIC_INTERVAL_MS = 3000;
const DIAGNOSTIC_TIMEOUT_MS = 2200;
const DIAGNOSTIC_STALE_MS = 10000;
const RTT_SAMPLE_LIMIT = 12;

const focusTypeLabel = (value) => {
    if (value === 'pricing') return 'Проверка цены';
    if (value === '3d') return '3D';
    if (value === 'matching') return 'Сопоставление';
    if (value === 'import') return 'Товары и цены';
    return '';
};

const userFocusLabel = (user = {}) => {
    if (user.focusLabel) return String(user.focusLabel);

    const type = focusTypeLabel(user.focusType);
    const number = Number(user.focusNumber || 0);

    if (type && number) return `${type} · №${number}`;
    if (type) return type;
    if (number) return `№${number}`;
    return 'На странице';
};

const roundMetric = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : fallback;
};

const averageJitter = (samples = []) => {
    if (!Array.isArray(samples) || samples.length < 2) return 0;

    let total = 0;
    for (let index = 1; index < samples.length; index += 1) {
        total += Math.abs(Number(samples[index] || 0) - Number(samples[index - 1] || 0));
    }

    return Math.round(total / Math.max(1, samples.length - 1));
};

const detectBrowserLabel = () => {
    if (typeof navigator === 'undefined') return '';

    const userAgent = String(navigator.userAgent || '');
    if (/Edg\//i.test(userAgent)) return 'Edge';
    if (/OPR\//i.test(userAgent)) return 'Opera';
    if (/Firefox\//i.test(userAgent)) return 'Firefox';
    if (/Chrome\//i.test(userAgent)) return 'Chrome';
    if (/Safari\//i.test(userAgent)) return 'Safari';
    return 'Браузер';
};

const detectDeviceLabel = () => {
    if (typeof navigator === 'undefined') return 'Устройство';

    const userAgent = String(navigator.userAgent || '');
    const platform = String(navigator.userAgentData?.platform || navigator.platform || '');

    if (/Android/i.test(userAgent)) {
        const model = userAgent.match(/Android\s[^;]+;\s*([^;)]+?)(?:\s+Build\/|;|\))/i)?.[1]
            ?.replace(/^wv$/i, '')
            ?.trim();
        return model ? `Android · ${model}` : 'Android';
    }

    if (/iPad/i.test(userAgent) || (/Mac/i.test(platform) && Number(navigator.maxTouchPoints || 0) > 1)) {
        return 'iPad';
    }
    if (/iPhone/i.test(userAgent)) return 'iPhone';
    if (/Windows/i.test(userAgent) || /Win/i.test(platform)) return 'Windows PC';
    if (/Mac/i.test(userAgent) || /Mac/i.test(platform)) return 'Mac';
    if (/Linux/i.test(userAgent) || /Linux/i.test(platform)) return 'Linux';

    return platform || 'Устройство';
};

const getSocketTransport = (socket) => {
    const name = String(socket?.io?.engine?.transport?.name || '').toLowerCase();
    if (name === 'websocket') return 'websocket';
    if (name === 'polling') return 'polling';
    return 'unknown';
};

const transportLabel = (value) => {
    if (value === 'websocket') return 'WS';
    if (value === 'polling') return 'POLL';
    return '—';
};

const connectionQuality = (connection = {}) => {
    const updatedAt = Number(connection.updatedAt || 0);
    const stale = updatedAt > 0 && Date.now() - updatedAt > DIAGNOSTIC_STALE_MS;
    const rtt = Number(connection.rttMs || 0);
    const jitter = Number(connection.jitterMs || 0);
    const revisionLag = Number(connection.revisionLag || 0);
    const mainThreadLag = Number(connection.mainThreadLagMs || 0);
    const fps = Number(connection.fps || 0);

    if (connection.timedOut === true || stale) {
        return { key: 'bad', label: stale ? 'Нет свежих данных' : 'Таймаут' };
    }
    if (mainThreadLag > 250 || (fps > 0 && fps <= 5)) {
        return { key: 'ui', label: 'Браузер перегружен' };
    }
    if (!(rtt > 0)) return { key: 'waiting', label: 'Измерение…' };
    if (rtt > 350 || jitter > 120 || revisionLag > 8) {
        return { key: 'bad', label: 'Плохое' };
    }
    if (rtt > 180 || jitter > 60 || revisionLag > 3) {
        return { key: 'warn', label: 'Нестабильно' };
    }
    if (rtt > 80 || jitter > 25 || revisionLag > 1) {
        return { key: 'fair', label: 'Нормально' };
    }
    return { key: 'good', label: 'Отлично' };
};

const normalizeDiagnosticConnection = (connection = {}) => ({
    socketId: String(connection.socketId || ''),
    userId: String(connection.userId || ''),
    name: String(connection.name || 'Администратор'),
    role: connection.role || null,
    deviceLabel: String(connection.deviceLabel || 'Устройство'),
    browserLabel: String(connection.browserLabel || ''),
    transport: String(connection.transport || 'unknown'),
    visibility: connection.visibility === 'hidden' ? 'hidden' : 'visible',
    rttMs: roundMetric(connection.rttMs),
    jitterMs: roundMetric(connection.jitterMs),
    fps: roundMetric(connection.fps),
    applyMs: Math.max(0, Number(connection.applyMs || 0)),
    mainThreadLagMs: roundMetric(connection.mainThreadLagMs),
    pendingQueue: roundMetric(connection.pendingQueue),
    reconnects: roundMetric(connection.reconnects),
    localRevision: roundMetric(connection.localRevision),
    serverRevision: roundMetric(connection.serverRevision),
    revisionLag: roundMetric(connection.revisionLag),
    timedOut: connection.timedOut === true,
    updatedAt: Number(connection.updatedAt || Date.now()),
});

const useAdminRealtimeDiagnostics = ({ enabled, pageKey, socketId }) => {
    const [connections, setConnections] = useState([]);
    const [local, setLocal] = useState(() => normalizeDiagnosticConnection({
        socketId,
        deviceLabel: detectDeviceLabel(),
        browserLabel: detectBrowserLabel(),
        visibility: typeof document !== 'undefined' ? document.visibilityState : 'visible',
    }));

    const metricsRef = useRef(local);
    const rttSamplesRef = useRef([]);
    const reconnectsRef = useRef(0);
    const seenConnectedRef = useRef(false);
    const fpsFrameRef = useRef(null);
    const fpsCounterRef = useRef(0);
    const fpsWindowStartedRef = useRef(0);
    const pingSequenceRef = useRef(0);
    const mainThreadLagRef = useRef(0);

    useEffect(() => {
        metricsRef.current = local;
    }, [local]);

    useEffect(() => {
        if (!enabled || !pageKey || typeof window === 'undefined') return undefined;

        let active = true;
        fpsCounterRef.current = 0;
        fpsWindowStartedRef.current = performance.now();

        const frame = (timestamp) => {
            if (!active) return;
            fpsCounterRef.current += 1;

            const elapsed = timestamp - fpsWindowStartedRef.current;
            if (elapsed >= 1000) {
                const fps = Math.max(0, Math.round((fpsCounterRef.current * 1000) / elapsed));
                fpsCounterRef.current = 0;
                fpsWindowStartedRef.current = timestamp;

                setLocal((previous) => {
                    const next = {
                        ...previous,
                        fps,
                        visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
                    };
                    metricsRef.current = next;
                    return next;
                });
            }

            fpsFrameRef.current = window.requestAnimationFrame(frame);
        };

        fpsFrameRef.current = window.requestAnimationFrame(frame);
        return () => {
            active = false;
            if (fpsFrameRef.current) window.cancelAnimationFrame(fpsFrameRef.current);
            fpsFrameRef.current = null;
        };
    }, [enabled, pageKey]);

    useEffect(() => {
        if (!enabled || !pageKey || typeof window === 'undefined') return undefined;

        const interval = 250;
        let expectedAt = performance.now() + interval;
        let timer = null;

        const sample = () => {
            const timestamp = performance.now();
            const drift = Math.max(0, timestamp - expectedAt);
            expectedAt = timestamp + interval;

            // Держим пик с плавным спадом. Если main thread завис на 2 секунды,
            // значение не исчезнет мгновенно на следующем тике и попадёт в
            // ближайшую telemetry-посылку.
            mainThreadLagRef.current = Math.round(Math.max(
                drift,
                Number(mainThreadLagRef.current || 0) * 0.68
            ));
        };

        timer = window.setInterval(sample, interval);
        return () => {
            if (timer) window.clearInterval(timer);
            mainThreadLagRef.current = 0;
        };
    }, [enabled, pageKey]);

    useEffect(() => {
        const socket = getSocket();
        const currentPageKey = String(pageKey || '');
        const deviceLabel = detectDeviceLabel();
        const browserLabel = detectBrowserLabel();
        let disposed = false;
        let pingTimer = null;
        let pendingTimeout = null;

        const replaceDiagnostics = (rows = []) => {
            const now = Date.now();
            const normalized = (Array.isArray(rows) ? rows : [])
                .map(normalizeDiagnosticConnection)
                .filter((row) => row.socketId)
                .filter((row) => !row.updatedAt || now - row.updatedAt < DIAGNOSTIC_STALE_MS * 3);

            setConnections(normalized);
        };

        const upsertConnection = (connection) => {
            const normalized = normalizeDiagnosticConnection(connection);
            if (!normalized.socketId) return;

            setConnections((previous) => {
                const next = new Map(previous.map((row) => [row.socketId, row]));
                next.set(normalized.socketId, normalized);
                return Array.from(next.values())
                    .filter((row) => Date.now() - Number(row.updatedAt || 0) < DIAGNOSTIC_STALE_MS * 3)
                    .sort((left, right) => Number(right.updatedAt || 0) - Number(left.updatedAt || 0));
            });
        };

        const removeConnection = (removedSocketId) => {
            const key = String(removedSocketId || '');
            if (!key) return;
            setConnections((previous) => previous.filter((row) => row.socketId !== key));
        };

        const onTelemetry = (payload = {}) => {
            if (String(payload.pageKey || '') !== currentPageKey) return;
            if (payload.removedSocketId) {
                removeConnection(payload.removedSocketId);
                return;
            }
            if (payload.connection) upsertConnection(payload.connection);
        };

        const onConnect = () => {
            if (seenConnectedRef.current) reconnectsRef.current += 1;
            seenConnectedRef.current = true;

            setLocal((previous) => {
                const next = {
                    ...previous,
                    socketId: String(socket.id || previous.socketId || ''),
                    transport: getSocketTransport(socket),
                    reconnects: reconnectsRef.current,
                    timedOut: false,
                };
                metricsRef.current = next;
                return next;
            });
        };

        const onDisconnect = () => {
            setLocal((previous) => {
                const next = {
                    ...previous,
                    transport: 'unknown',
                    timedOut: true,
                    updatedAt: Date.now(),
                };
                metricsRef.current = next;
                return next;
            });
        };

        const finalizePing = ({ response = null, timedOut = false, startedAt = 0 }) => {
            if (disposed) return;

            const runtime = getAdminRealtimeRuntimeDiagnostics();
            const rttMs = timedOut
                ? 0
                : Math.max(0, Math.round(performance.now() - startedAt));

            if (rttMs > 0) {
                rttSamplesRef.current.push(rttMs);
                if (rttSamplesRef.current.length > RTT_SAMPLE_LIMIT) {
                    rttSamplesRef.current.splice(
                        0,
                        rttSamplesRef.current.length - RTT_SAMPLE_LIMIT
                    );
                }
            }

            const serverRevision = roundMetric(response?.snapshotRevision);
            const localRevision = roundMetric(runtime.localRevision);
            const next = normalizeDiagnosticConnection({
                ...metricsRef.current,
                socketId: String(socket.id || socketId || metricsRef.current.socketId || ''),
                deviceLabel,
                browserLabel,
                transport: getSocketTransport(socket),
                visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
                rttMs,
                jitterMs: averageJitter(rttSamplesRef.current),
                applyMs: runtime.applyMs,
                mainThreadLagMs: mainThreadLagRef.current,
                pendingQueue: runtime.pendingQueue,
                reconnects: reconnectsRef.current,
                localRevision,
                serverRevision,
                revisionLag: Math.max(0, serverRevision - localRevision),
                timedOut,
                updatedAt: Date.now(),
            });

            metricsRef.current = next;
            setLocal(next);

            if (Array.isArray(response?.diagnostics)) {
                replaceDiagnostics(response.diagnostics);
            }

            if (!timedOut && socket.connected && currentPageKey) {
                socket.emit('adminRealtime:telemetry', {
                    token: window.localStorage.getItem('token'),
                    pageKey: currentPageKey,
                    ...next,
                });
            }
        };

        const sendPing = () => {
            if (
                disposed
                || !enabled
                || !currentPageKey
                || !socket.connected
            ) {
                return;
            }

            const sequence = ++pingSequenceRef.current;
            const startedAt = performance.now();
            let completed = false;

            if (pendingTimeout) window.clearTimeout(pendingTimeout);
            pendingTimeout = window.setTimeout(() => {
                if (completed || sequence !== pingSequenceRef.current) return;
                completed = true;
                finalizePing({ timedOut: true, startedAt });
            }, DIAGNOSTIC_TIMEOUT_MS);

            socket.emit('adminRealtime:diagnosticPing', {
                token: window.localStorage.getItem('token'),
                pageKey: currentPageKey,
                clientSentAt: Date.now(),
            }, (response = {}) => {
                if (completed || sequence !== pingSequenceRef.current) return;
                completed = true;
                if (pendingTimeout) window.clearTimeout(pendingTimeout);
                pendingTimeout = null;

                finalizePing({
                    response: response?.ok ? response : null,
                    timedOut: response?.ok !== true,
                    startedAt,
                });
            });
        };

        socket.on('adminRealtime:telemetry', onTelemetry);
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);

        if (socket.connected) {
            seenConnectedRef.current = true;
            setLocal((previous) => {
                const next = {
                    ...previous,
                    socketId: String(socket.id || socketId || previous.socketId || ''),
                    transport: getSocketTransport(socket),
                    reconnects: reconnectsRef.current,
                    timedOut: false,
                };
                metricsRef.current = next;
                return next;
            });
        }

        setConnections([]);
        if (enabled && currentPageKey) {
            sendPing();
            pingTimer = window.setInterval(sendPing, DIAGNOSTIC_INTERVAL_MS);
        }

        return () => {
            disposed = true;
            if (pingTimer) window.clearInterval(pingTimer);
            if (pendingTimeout) window.clearTimeout(pendingTimeout);
            socket.off('adminRealtime:telemetry', onTelemetry);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
        };
    }, [enabled, pageKey, socketId]);

    return { local, connections };
};

const ConnectionDiagnostics = ({ connection, ownSocketId }) => {
    const quality = connectionQuality(connection);
    const isOwn = String(connection.socketId || '') === String(ownSocketId || '');
    const hidden = connection.visibility === 'hidden';

    return (
        <div className={`adminRealtimePanel__device is-${quality.key} ${hidden ? 'is-hidden' : ''}`}>
            <span className="adminRealtimePanel__deviceDot" />
            <div className="adminRealtimePanel__deviceMain">
                <div>
                    <strong>{connection.deviceLabel || 'Устройство'}</strong>
                    {isOwn ? <em>этот экран</em> : null}
                    {hidden ? <em>фон</em> : null}
                </div>
                <small>
                    {connection.browserLabel || 'Браузер'} · {transportLabel(connection.transport)} · {quality.label}
                </small>
            </div>
            <div className="adminRealtimePanel__deviceMetrics">
                <strong>{connection.rttMs > 0 ? `${connection.rttMs} ms` : '—'}</strong>
                <span>{connection.fps > 0 ? `${connection.fps} FPS` : 'FPS —'}</span>
            </div>
            <div className="adminRealtimePanel__deviceDetails">
                <span>jitter {connection.jitterMs || 0} ms</span>
                <span>apply {Number(connection.applyMs || 0).toFixed(1)} ms</span>
                <span>ui {connection.mainThreadLagMs || 0} ms</span>
                <span>rev {connection.localRevision || 0}/{connection.serverRevision || 0}</span>
                <span>lag {connection.revisionLag || 0}</span>
                <span>queue {connection.pendingQueue || 0}</span>
                <span>reconnect {connection.reconnects || 0}</span>
            </div>
        </div>
    );
};

const AdminRealtimePanel = ({ panelRef, diagnostics, ownSocketId }) => {
    const {
        source,
        closePanel,
        toggleCurrentSourceEnabled,
    } = useAdminRealtimePanel();

    const users = source?.users || [];
    const enabled = source?.enabled !== false;
    const connected = enabled && source?.connected === true;
    const toggleAvailable = source?.toggleAvailable === true;

    const connectionTitle = !enabled
        ? 'Совместная работа выключена'
        : (connected ? 'Соединение активно' : 'Нет активного соединения');

    const connectionDescription = !enabled
        ? 'Курсоры, общий скролл, камера, локи и синхронизация экрана отключены только у вас.'
        : (source?.roomLabel || 'На этой странице совместная комната не подключена');

    const connectionsForUser = (user = {}) => diagnostics.filter((connection) => {
        const userId = String(user.userId || '');
        if (userId && String(connection.userId || '') === userId) return true;
        return String(connection.socketId || '') === String(user.socketId || '');
    });

    return (
        <section
            ref={panelRef}
            className="adminRealtimePanel"
            role="dialog"
            aria-modal="false"
            aria-label="Состояние совместной работы"
        >
            <header className="adminRealtimePanel__head">
                <div>
                    <span>Сокеты</span>
                    <strong>{source?.title || 'Совместная работа'}</strong>
                </div>

                <button
                    type="button"
                    className="adminRealtimePanel__close"
                    onClick={closePanel}
                    aria-label="Свернуть панель сокетов"
                >
                    ×
                </button>
            </header>

            <div className={`adminRealtimePanel__connection ${!enabled ? 'is-disabled' : (connected ? 'is-online' : 'is-offline')}`}>
                <span className="adminRealtimePanel__statusDot" />
                <div>
                    <strong>{connectionTitle}</strong>
                    <small>{connectionDescription}</small>
                </div>
            </div>

            {source && toggleAvailable ? (
                <div className="adminRealtimePanel__switchRow">
                    <div>
                        <strong>Совместная работа</strong>
                        <small>
                            {enabled
                                ? 'Вы участвуете в общей комнате.'
                                : 'Страница работает локально без realtime-участия.'}
                        </small>
                    </div>
                    <button
                        type="button"
                        className={`adminRealtimePanel__switch ${enabled ? 'is-enabled' : 'is-disabled'}`}
                        onClick={toggleCurrentSourceEnabled}
                        aria-pressed={enabled}
                    >
                        <span />
                        {enabled ? 'Отключить' : 'Подключить'}
                    </button>
                </div>
            ) : null}

            {source ? (
                <>
                    <div className="adminRealtimePanel__context">
                        {source.pageLabel ? (
                            <div>
                                <span>Раздел</span>
                                <strong>{source.pageLabel}</strong>
                            </div>
                        ) : null}

                        {source.categoryLabel ? (
                            <div>
                                <span>Категория</span>
                                <strong>{source.categoryLabel}</strong>
                            </div>
                        ) : null}

                        {source.focusLabel ? (
                            <div>
                                <span>Открыто</span>
                                <strong>{source.focusLabel}</strong>
                            </div>
                        ) : null}
                    </div>

                    <div className="adminRealtimePanel__summary">
                        <div>
                            <span>В комнате</span>
                            <strong>{enabled ? users.length : 0}</strong>
                        </div>
                        <div>
                            <span>Соединений</span>
                            <strong>{enabled ? diagnostics.length : 0}</strong>
                        </div>
                        <div>
                            <span>Операций</span>
                            <strong>{enabled ? (source.operationsCount || 0) : 0}</strong>
                        </div>
                        <div>
                            <span>Занято полей</span>
                            <strong>{enabled ? (source.lockedFieldsCount || 0) : 0}</strong>
                        </div>
                    </div>

                    <div className="adminRealtimePanel__users">
                        <div className="adminRealtimePanel__sectionTitle">
                            <span>Участники и качество соединения</span>
                            <strong>{enabled ? users.length : 0}</strong>
                        </div>

                        {!enabled ? (
                            <div className="adminRealtimePanel__empty">
                                Подключите совместную работу, чтобы видеть участников.
                            </div>
                        ) : users.length ? users.map((user, index) => {
                            const userConnections = connectionsForUser(user);

                            return (
                                <div
                                    className="adminRealtimePanel__user"
                                    key={user.socketId || `${user.userId}-${index}`}
                                >
                                    <span className="adminRealtimePanel__avatar">
                                        {String(user.name || 'А').slice(0, 1).toUpperCase()}
                                    </span>
                                    <div>
                                        <strong>{user.name || 'Администратор'}</strong>
                                        <small>{userFocusLabel(user)}</small>

                                        <div className="adminRealtimePanel__devices">
                                            {userConnections.length ? userConnections.map((connection) => (
                                                <ConnectionDiagnostics
                                                    key={connection.socketId}
                                                    connection={connection}
                                                    ownSocketId={ownSocketId}
                                                />
                                            )) : (
                                                <div className="adminRealtimePanel__deviceWaiting">
                                                    Измеряем ping и состояние этого подключения…
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <span className="adminRealtimePanel__onlineDot" />
                                </div>
                            );
                        }) : (
                            <div className="adminRealtimePanel__empty">
                                {connected
                                    ? 'В комнате пока нет других подключений.'
                                    : 'Подключение появится после входа в совместный раздел.'}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="adminRealtimePanel__empty adminRealtimePanel__empty--large">
                    На текущей странице совместный realtime-режим не используется.
                </div>
            )}
        </section>
    );
};

const AdminRealtimeHud = ({ diagnostic, visible }) => {
    if (!visible) return null;

    const quality = connectionQuality(diagnostic);
    const revisionText = diagnostic.serverRevision > 0
        ? `${diagnostic.localRevision || 0}/${diagnostic.serverRevision}`
        : '—';

    return (
        <div
            className={`adminRealtimeHud is-${quality.key}`}
            title={[
                `Соединение: ${quality.label}`,
                `Jitter: ${diagnostic.jitterMs || 0} ms`,
                `Применение realtime: ${Number(diagnostic.applyMs || 0).toFixed(1)} ms`,
                `Main thread lag: ${diagnostic.mainThreadLagMs || 0} ms`,
                `Очередь: ${diagnostic.pendingQueue || 0}`,
                `Reconnect: ${diagnostic.reconnects || 0}`,
            ].join('\n')}
        >
            <span className="adminRealtimeHud__dot" />
            <span><small>FPS</small><strong>{diagnostic.fps || '—'}</strong></span>
            <span><small>NET</small><strong>{diagnostic.rttMs > 0 ? `${diagnostic.rttMs}ms` : '—'}</strong></span>
            <span><small>UI</small><strong>{diagnostic.mainThreadLagMs > 0 ? `${diagnostic.mainThreadLagMs}ms` : '0'}</strong></span>
            <span><small>LINK</small><strong>{transportLabel(diagnostic.transport)}</strong></span>
            <span><small>REV</small><strong>{revisionText}</strong></span>
            <span><small>Q</small><strong>{diagnostic.pendingQueue || 0}</strong></span>
        </div>
    );
};

const AdminRealtimePanelHeaderControl = () => {
    const {
        source,
        isOpen,
        closePanel,
        togglePanel,
        uiBridgeStatus,
    } = useAdminRealtimePanel();

    const buttonRef = useRef(null);
    const panelRef = useRef(null);

    const enabled = source?.enabled !== false;
    const count = enabled ? (source?.users?.length || 0) : 0;
    const connected = enabled && source?.connected === true;
    const pageKey = String(uiBridgeStatus?.pageKey || source?.sourceId || '');
    const ownSocketId = String(uiBridgeStatus?.socketId || '');
    const diagnostics = useAdminRealtimeDiagnostics({
        enabled: Boolean(source && enabled),
        pageKey,
        socketId: ownSocketId,
    });

    const buttonTitle = useMemo(() => {
        if (!source) return 'Сокеты: на этой странице нет активной комнаты';
        if (!enabled) return 'Совместная работа выключена';
        return connected
            ? `Сокеты подключены · ${count}`
            : 'Сокеты: нет соединения';
    }, [connected, count, enabled, source]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (panelRef.current?.contains(event.target)) return;
            if (buttonRef.current?.contains(event.target)) return;
            closePanel();
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closePanel();
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closePanel, isOpen]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={`app-header__realtimeBtn ${!enabled ? 'is-disabled' : (connected ? 'is-online' : 'is-offline')} ${isOpen ? 'is-open' : ''}`}
                onClick={togglePanel}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                title={buttonTitle}
            >
                <span className="app-header__realtimeDot" />
                <span className="app-header__realtimeLabel">Сокеты</span>
                <strong>{count}</strong>
            </button>

            {typeof document !== 'undefined'
                ? createPortal(
                    <AdminRealtimeHud
                        diagnostic={diagnostics.local}
                        visible={Boolean(source && enabled)}
                    />,
                    document.body
                )
                : null}

            {isOpen && typeof document !== 'undefined'
                ? createPortal(
                    <AdminRealtimePanel
                        panelRef={panelRef}
                        diagnostics={diagnostics.connections}
                        ownSocketId={ownSocketId}
                    />,
                    document.body
                )
                : null}
        </>
    );
};

export default AdminRealtimePanelHeaderControl;
